import { Flex } from 'antd';
import { BaseButton } from 'components/button/button-base';
import { Link } from 'components/mvp-typography';
import { getAppVersion } from 'lib/api/firebase/api-_utils';

import { QuestionnaireIntro } from 'features/patient/assessment/assessment-controller/_components/questionnaire-intro';
import { QAPanelTemplate } from 'features/patient/assessment/assessment-controller/_components/questionnaire-qa-panel';
import { QuestionnaireDataProps } from 'features/patient/assessment/assessment-controller/types';
import { useState } from 'react';
import { prepareImPACTTest } from '../_utils/prepare-impact-test';
import { ImPACTSlice } from '../impactAPISlice';
import { APIImpact } from 'lib/api/firebase/apis/api-integration-impact';
import { useMutation } from '@tanstack/react-query';
import { ErrorListDisplay } from 'components/ErrorDisplay';
import {
  ErrorListContext,
  useErrorList,
  useErrorListContext
} from 'lib/utils/useErrorList';
import { resolveError } from 'lib/utils/formatters/error/resolve-error';

export function ImpactQuestionnaireIntro(props: {
  impactData: QuestionnaireDataProps;
  impactInfo: ImPACTSlice;
  userId: string;
  /**
   * If this value is set or not undefined the component will
   * try to use the value when creating an ImPACT test URL
   */
  existingPassportId: string | null | -1;
}) {
  const [impactTestUrl, setImpactTestUrl] = useState<string | null>(null);
  const errorList = useErrorList();

  async function startImpactQuestionnaire() {
    const testUrl = await prepareImPACTTest(props);
    console.log('[startImpactQuestionnaire]', testUrl);

    if (testUrl) {
      openImPactTest(testUrl);
    }
  }

  function openImPactTest(url: string) {
    setImpactTestUrl(url);
    try {
      window.open(url, '_blank', 'noreferer');
    } catch (e) {
      throw new Error(`Failed to open a new window`);
    }
  }

  return (
    <Flex vertical gap={16}>
      <QuestionnaireIntro
        data={props.impactData}
        startQuestionnaire={
          /**
           * Trigger the function if:
           * 1. The user has an existing passport ID
           * 2. The user set an existing passport ID
           * 3. THe user did not set an existing passport ID
           */
          props.impactInfo.verified || props.existingPassportId !== null
            ? startImpactQuestionnaire
            : /**
               * TODO: Show an error message
               */
              undefined
        }
      />

      <Flex align="center" vertical gap={16}>
        <QAPanelTemplate>
          <Flex vertical gap={16}>
            <ErrorListContext value={{ errorList }}>
              <div>
                <SkipImpactTestButton openImpactTest={openImPactTest} />
              </div>
            </ErrorListContext>

            <ErrorListDisplay errorList={errorList} />
          </Flex>
        </QAPanelTemplate>

        {impactTestUrl && (
          <Link href={impactTestUrl}>Page didn&apos;t open? Click here</Link>
        )}
      </Flex>
    </Flex>
  );
}

function SkipImpactTestButton(props: {
  openImpactTest: (url: string) => void;
}) {
  const errorListContext = useErrorListContext();
  /**
   * Handler to skip ImPACT tests
   * @param passportId
   * @returns
   */
  const fakeImpactTestMutation = useMutation({
    mutationFn: async () => {
      const fakeData = await APIImpact.generateFakeSubmitUrl();

      const url = new URL(
        // !IS_LOCAL_ENDPOINT
        //   ? 'http://127.0.0.1:5001/concussionrx-develop/us-central1/v2/impact/receiveImpactScores' :
        `${process.env.REACT_APP_CLOUD_SERVER}/v2/impact/receiveImpactScores`
      );
      const params = new URLSearchParams(fakeData.data);
      params.append(
        '_concussionrx_appVersion',
        getAppVersion() ?? 'FATAL: NO APP VERSION SET'
      );

      url.search = params.toString();
      props.openImpactTest(url.toString());
    },
    onError(error) {
      const e = resolveError(error);
      errorListContext.errorList.addError(
        e,
        'Fake ImPACTTest Mutation',
        'Fake ImPACTTest Mutation'
      );
    }
  });

  return (
    <BaseButton
      loading={fakeImpactTestMutation.isLoading}
      disabled={fakeImpactTestMutation.isLoading}
      onClick={() => fakeImpactTestMutation.mutate()}
    >
      Skip ImPACT Test
    </BaseButton>
  );
}
